import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Logo } from "@/components/Logo";
import { toast } from "sonner";
import { Loader2, Languages } from "lucide-react";

const EXAM_LANGS = [
  { code: "EN", label: "English" },
  { code: "IT", label: "Italian" },
  { code: "DE", label: "German" },
  { code: "FR", label: "French" },
  { code: "HU", label: "Hungarian" },
  { code: "BG", label: "Bulgarian" },
  { code: "RO", label: "Romanian" },
  { code: "CS", label: "Czech" },
  { code: "NL", label: "Dutch" },
  { code: "ES", label: "Spanish" },
  { code: "PL", label: "Polish" },
  { code: "PT", label: "Portuguese" },
];

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [examLang, setExamLang] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!examLang) { setError("Please select your exam language."); return; }
    if (!gdpr) { setError("You must accept the Privacy Policy and Terms."); return; }
    setLoading(true); setError("");
    try {
      await register({ ...form, exam_language: examLang, gdpr_consent: gdpr, marketing_consent: marketing });
      toast.success("Account created!");
      nav("/account");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || "Sign up failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12" data-testid="register-page">
      <div className="w-full max-w-md glass rounded-2xl p-8">
        <div className="flex justify-center mb-6"><Logo className="h-12" showText /></div>
        <h1 className="font-display text-2xl font-bold text-white text-center">Create account</h1>
        <p className="text-sm text-slate-400 text-center mt-1">An account is required to purchase and take exams.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-slate-300">Full name <span className="text-slate-500 font-normal">(this exact name will appear on your certificate)</span></Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name-input"
              placeholder="e.g. Jane A. Sample"
              className="bg-surface-2 border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Email</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input"
              className="bg-surface-2 border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-slate-300">Password</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input"
              className="bg-surface-2 border-white/10 text-white mt-1" />
          </div>
          <div>
            <Label className="text-slate-300 flex items-center gap-1.5"><Languages className="h-4 w-4 text-gold" />Exam language</Label>
            <Select value={examLang} onValueChange={setExamLang}>
              <SelectTrigger className="bg-surface-2 border-white/10 text-white mt-1" data-testid="register-language-select">
                <SelectValue placeholder="Select your language" />
              </SelectTrigger>
              <SelectContent>
                {EXAM_LANGS.map((l) => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-1.5">
              Course materials are in English. Your exam will be <span className="text-gold">bilingual</span> — in English and the language you select here.
            </p>
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="gdpr" checked={gdpr} onCheckedChange={setGdpr} data-testid="register-gdpr-checkbox" className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
            <label htmlFor="gdpr" className="text-xs text-slate-300 leading-relaxed">
              I agree to the <Link to="/policies/privacy" className="text-gold underline">Privacy Policy</Link> and <Link to="/policies/terms" className="text-gold underline">Terms & Conditions</Link>.
            </label>
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="mk" checked={marketing} onCheckedChange={setMarketing} data-testid="register-marketing-checkbox" className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
            <label htmlFor="mk" className="text-xs text-slate-400 leading-relaxed">I'd like to receive news about courses (optional).</label>
          </div>
          {error && <p className="text-sm text-rose-400" data-testid="register-error">{error}</p>}
          <Button type="submit" className="w-full btn-gold h-11" disabled={loading} data-testid="register-submit-button">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Create account
          </Button>
        </form>
        <p className="text-sm text-slate-400 text-center mt-6">
          Already have an account? <Link to="/login" className="text-gold hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
